import {
  type AppKit,
  createAppKit,
  Features,
  type Metadata as _Metadata,
  type AccountState
} from '@reown/appkit'
import {
  ChainController,
  ConnectionController,
  CoreHelperUtil,
  RouterController
} from '@reown/appkit-core'
import {
  type AppKitNetwork,
  bsc,
  bscTestnet,
  mainnet,
  sepolia
} from '@reown/appkit/networks'
import { EthersAdapter } from '@reown/appkit-adapter-ethers'
import {
  BrowserProvider,
  parseUnits,
  Contract,
  formatUnits,
  type Eip1193Provider,
  makeError
} from 'ethers'
import { erc20Abi } from 'viem'
import { makeAppkitError } from './error'
import { registerSelectWalletButton } from './w3m-connecting-wc-qrcode-with-select-wallet'

/**
 * Reown does not support Tron yet,
 * see [issue](https://github.com/orgs/reown-com/discussions/2427#discussioncomment-9788169)
 */
const networkMap = {
  // EVM
  mainnet,
  sepolia,
  bsc,
  bscTestnet

  // Tron
  // tron,

  // Solana
  // solana,
  // solanaTestnet
} as const

type NetworkName = keyof typeof networkMap

type Metadata = Omit<_Metadata, 'url'> & Partial<Pick<_Metadata, 'url'>>

export type ReownAppkitParams = {
  projectId: string
  metadata: Metadata
  networks: [NetworkName, ...NetworkName[]]
  features?: Features
}

export type Account = Pick<AccountState, 'address' | 'status'> &
  Record<
    'isConnected' | 'isConnecting' | 'isDisconnected' | 'isReconnecting',
    boolean
  >

const isAccountEqual = (a: Account, b: Account) =>
  a.address === b.address && a.status === b.status

export type Hex = `0x${string}`

export type ReownAppkit = {
  modal: AppKit

  connect(params?: { timeout?: number }): Promise<Account>
  connectByWc(params?: { timeout?: number }): Promise<Account>
  connectByWcUri(params?: { timeout?: number }): Promise<Account>
  disconnect(): Promise<void>
  getAccount(): Account
  onAccount(callback: (v: Account) => void): () => void
  getNetwork(): NetworkName
  setNetwork(networkName: NetworkName): Promise<void>
  pickNetwork(): Promise<NetworkName>
  signMessage(params: { message: string }): Promise<Hex>
  erc20BalanceOf(params: {
    contractAddress: Hex
    decimals?: number
    account?: Hex
  }): Promise<string>
  erc20Transfer(params: {
    contractAddress: Hex
    recipient: Hex
    amount: string
    decimals?: number
  }): Promise<Hex>
}

let store: ReownAppkit | null = null

export function makeReownAppkit({
  projectId,
  metadata,
  networks: networkNames,
  features
}: ReownAppkitParams): ReownAppkit | null {
  if (!projectId) return null
  if (!networkNames.length) return null
  if (store) return store

  registerSelectWalletButton()

  const _metadata = { url: window.location.origin, ...metadata }
  const networks = networkNames
    .map(name => networkMap[name] as AppKitNetwork)
    .filter(v => !!v) as [AppKitNetwork, ...AppKitNetwork[]]

  const ethersAdapter = new EthersAdapter()

  const modal = createAppKit({
    adapters: [ethersAdapter],
    networks,
    metadata: _metadata,
    projectId,
    features
  })

  // 避免以下原因影響到 w3-modal 不會打開或要點多次才打開
  // vite web 偶爾沒有 click event
  // flutter web 會有錯誤的 click event target
  const _open = modal.open.bind(modal)
  const open: typeof modal.open = async options => {
    setTimeout(() => _open(options), 100)
  }
  modal.open = open

  const connect = async ({ timeout }: { timeout?: number } = {}) => {
    const account = getAccount()
    if (account.status === 'connected') return account

    modal.open({ view: 'Connect' })

    let finish: (cb?: () => void) => void = () => {}

    return new Promise<Account>((resolve, reject) => {
      const unsubscribe = modal.subscribeEvents(v => {
        switch (v.data.event) {
          case 'MODAL_CLOSE':
          case 'CONNECT_SUCCESS': {
            return finish(() => setTimeout(() => resolve(getAccount()), 100))
          }
          case 'CONNECT_ERROR': {
            // TODO error code
            const error = new Error(v.data.properties.message)
            return finish(() => reject(error))
          }
          default:
            return
        }
      })

      if (!!timeout && timeout > 0) {
        // TODO error code
        setTimeout(() => finish(() => reject(new Error('timeout'))), timeout)
      }

      finish = (cb = () => {}) => {
        if (modal.isOpen()) modal.close()
        unsubscribe()
        cb()
      }
    })
  }

  /**
   * open modal to wc qrcode view
   */
  const connectByWc = async ({ timeout }: { timeout?: number } = {}) => {
    const account = getAccount()
    if (account.status === 'connected') return account

    modal.open({ view: 'Connect' })

    const provider = await modal.getUniversalProvider().catch(() => undefined)

    // TODO error code
    if (!provider) throw new Error('UniversalProvider is required')

    const { wcPairingExpiry } = ConnectionController.state

    const canUseLastWcUri =
      (CoreHelperUtil.isTelegram() ||
        (CoreHelperUtil.isSafari() && CoreHelperUtil.isIos())) &&
      !CoreHelperUtil.isPairingExpired(wcPairingExpiry)

    if (canUseLastWcUri) {
      setTimeout(() => RouterController.push('ConnectingWalletConnect'), 1000)
    } else {
      provider.once('display_uri', () =>
        RouterController.push('ConnectingWalletConnect')
      )
    }

    ConnectionController.connectWalletConnect().catch(() => {})

    let finish: (cb?: () => void) => void = () => {}

    return new Promise<Account>((resolve, reject) => {
      const unsubscribe = modal.subscribeEvents(v => {
        switch (v.data.event) {
          case 'MODAL_CLOSE':
          case 'CONNECT_SUCCESS': {
            return finish(() => setTimeout(() => resolve(getAccount()), 100))
          }
          case 'CONNECT_ERROR': {
            const error = new Error(v.data.properties.message)
            // TODO error code
            return finish(() => reject(error))
          }
          default:
            return
        }
      })

      if (!!timeout && timeout > 0) {
        setTimeout(() => finish(() => reject(new Error('timeout'))), timeout)
      }

      finish = (cb = () => {}) => {
        if (modal.isOpen()) modal.close()
        unsubscribe()
        cb()
      }
    })
  }

  let cancelLastWcUri: (...args: never[]) => void = () => {}

  // TODO 正確訂閱連接上的事件
  // BUG 用戶拒連，AccountController 卻收到連接事件，暫時用取消上次請求方式處理。
  /**
   * open wc deeplink in android and ios wallet webview
   */
  const connectByWcUri = async ({ timeout }: { timeout?: number } = {}) => {
    const isSupported =
      CoreHelperUtil.isMobile() &&
      (CoreHelperUtil.isAndroid() || !CoreHelperUtil.isSafari())
    // TODO error code
    if (!isSupported) throw new Error('WcUri is not supported')

    const account = getAccount()
    if (account.status === 'connected') return account

    const provider = await modal.getUniversalProvider().catch(() => undefined)

    // TODO error code
    if (!provider) throw new Error('UniversalProvider is required')

    const { wcPairingExpiry, wcUri } = ConnectionController.state

    /**
     * isTelegram 和 iOS 在沒過期時，不會觸發 display_uri 事件。
     * 見 [ConnectionController.connectWalletConnect()](https://github.com/reown-com/appkit/blob/28da17bb38ea16d605e02ce494cffa2b0f70eaa5/packages/controllers/src/controllers/ConnectionController.ts#L108)
     */
    const canUseLastWcUri =
      (CoreHelperUtil.isTelegram() ||
        (CoreHelperUtil.isSafari() && CoreHelperUtil.isIos())) &&
      !CoreHelperUtil.isPairingExpired(wcPairingExpiry)

    const onDisplayUri = (uri: string) => CoreHelperUtil.openHref(uri, '_self')

    if (canUseLastWcUri) {
      onDisplayUri(wcUri!)
    } else {
      provider.once('display_uri', onDisplayUri)
    }

    // 觸發底層 client.connectWalletConnect() 建立 wcConnectionPromise, 等待 display_uri
    ConnectionController.connectWalletConnect().catch(() => {})

    let finish: (cb?: () => void) => void = () => {}
    cancelLastWcUri()

    return new Promise<Account>((resolve, reject) => {
      const unsubscribe = modal.subscribeEvents(v => {
        switch (v.data.event) {
          case 'MODAL_OPEN':
          case 'MODAL_CLOSE':
          case 'CONNECT_SUCCESS': {
            return finish(() => setTimeout(() => resolve(getAccount()), 100))
          }
          case 'CONNECT_PROXY_ERROR':
          case 'CONNECT_ERROR': {
            // TODO error code
            const error = new Error(v.data.properties.message)
            return finish(() => reject(error))
          }
          default:
            return
        }
      })

      if (!!timeout && timeout > 0) {
        // TODO error code
        setTimeout(() => finish(() => reject(new Error('timeout'))), timeout)
      }

      cancelLastWcUri = finish = (cb = () => {}) => {
        provider.off('display_uri', onDisplayUri)
        unsubscribe()
        cb()
      }
    })
  }

  const pickNetwork = async () => {
    modal.open({ view: 'Networks' })

    return new Promise<NetworkName>((resolve, reject) => {
      const unsubscribe = modal.subscribeEvents(v => {
        switch (v.data.event) {
          case 'MODAL_CLOSE': {
            try {
              resolve(getNetwork())
            } catch (error) {
              reject(error)
            }
            return unsubscribe()
          }
          default:
            return
        }
      })
    })
  }

  const disconnect = async () => modal.disconnect()

  const getAccount = (): Account => {
    const reownAccount = ChainController.getAccountData()

    return {
      address: reownAccount?.address,
      status: reownAccount?.status,
      isConnected: reownAccount?.status === 'connected',
      isConnecting: reownAccount?.status === 'connecting',
      isDisconnected: reownAccount?.status === 'disconnected',
      isReconnecting: reownAccount?.status === 'reconnecting'
    }
  }

  const onAccount: ReownAppkit['onAccount'] = cb => {
    let before = getAccount()
    cb(before)

    return ChainController.subscribe(
      debounce(() => {
        const after = getAccount()
        if (isAccountEqual(before, after)) return

        before = after
        cb(after)
      })
    )
  }

  // TODO 測試切換網路至 dapp 不支援的網路，檢查 getNetworkName getAccount
  // TODO 用戶允許同時連線多個帳號

  // TODO Only wallet-available chains are supported, otherwise a user rejection error will be returned.
  const setNetwork = async (networkName: NetworkName) => {
    try {
      await modal.switchNetwork(networkMap[networkName])
    } catch (error) {
      throw makeAppkitError(error)
    }
  }

  const getNetwork = (): NetworkName => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, namespace, id] =
      modal.getState().selectedNetworkId?.match(/^(.+?):(.+)$/) ?? []
    const [name] =
      Object.entries(networkMap).find(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([_, chain]) =>
          id === chain.id.toString() &&
          ('chainNamespace' in chain
            ? chain.chainNamespace === namespace
            : true)
      ) ?? []
    return name as NetworkName
  }

  const signMessage: ReownAppkit['signMessage'] = async params => {
    const walletProvider = modal.getWalletProvider() as
      | Eip1193Provider
      | undefined

    if (!walletProvider)
      throw makeAppkitError(makeError('provider not found', 'CANCELLED'))

    const ethersProvider = new BrowserProvider(walletProvider)
    const signer = await ethersProvider.getSigner()
    const tx = await signer.signMessage(params.message)

    console.log('signMessage', { tx })

    return tx as Hex
  }

  const erc20BalanceOf: ReownAppkit['erc20BalanceOf'] = async ({
    contractAddress,
    decimals,
    account
  }) => {
    const address = account ?? (getAccount().address as Hex | undefined)
    if (!address)
      throw makeAppkitError(
        makeError('account is required', 'INVALID_ARGUMENT', {
          argument: 'account',
          value: account
        })
      )

    const walletProvider = modal.getWalletProvider() as
      | Eip1193Provider
      | undefined
    if (!walletProvider)
      throw makeAppkitError(makeError('provider not found', 'CANCELLED'))

    try {
      const ethersProvider = new BrowserProvider(walletProvider)
      const contract = new Contract(contractAddress, erc20Abi, ethersProvider)
      const _decimals =
        typeof decimals === 'number' && decimals > 0
          ? decimals
          : ((await contract.decimals()) as number)
      const data = await contract.balanceOf(address)

      return formatUnits(data, _decimals)
    } catch (error) {
      throw makeAppkitError(error)
    }
  }

  const erc20Transfer: ReownAppkit['erc20Transfer'] = async ({
    contractAddress,
    recipient,
    amount,
    decimals
  }) => {
    const account = getAccount()
    if (account.status !== 'connected')
      throw makeAppkitError(makeError('not connect', 'CANCELLED'))

    const walletProvider = modal.getWalletProvider() as
      | Eip1193Provider
      | undefined
    if (!walletProvider)
      throw makeAppkitError(makeError('provider not found', 'CANCELLED'))

    try {
      const ethersProvider = new BrowserProvider(walletProvider)
      const signer = await ethersProvider.getSigner()
      const contract = new Contract(contractAddress, erc20Abi, signer)
      const _decimals =
        typeof decimals === 'number' && decimals > 0
          ? decimals
          : ((await contract.decimals()) as number)
      const tx = await contract.transfer(
        recipient,
        parseUnits(amount, _decimals)
      )

      return tx.hash as Hex
    } catch (error) {
      throw makeAppkitError(error)
    }
  }

  store = {
    modal,
    connect,
    connectByWc,
    connectByWcUri,
    disconnect,
    getAccount,
    onAccount,
    signMessage,
    erc20BalanceOf,
    erc20Transfer,
    setNetwork,
    getNetwork,
    pickNetwork
  }

  return store
}

const debounce = <
  Agrs extends unknown[],
  Fn extends (...args: Agrs) => unknown
>(
  func: Fn,
  delay = 10
) => {
  let timer: ReturnType<typeof setTimeout> | null = null
  return (...args: Agrs) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => func(...args), delay)
  }
}
